// Need to use the React-specific entry point to import createApi
import _ from "lodash";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
    startSession,
    startCourseAttempt,
    startDeckAttempt,
} from "../../redux/attemptSlice";
import { organizeArticles } from "../../redux/articlesSlice";
import { settings } from "../settings/app.settings";
import { acceptTerms, updateBranding, updateUser } from "../../redux/authSlice";

export const baseUrl = settings.baseUrl;
export const clientAssetsPath = settings.clientAssetsPath;
export const targetRoute = settings.targetRoute;

function getChanged(array, key) {
    return _.uniq(
        _.map(
            _.filter(array, (element) => _.has(element, `payload.${key}`)),
            (element) => _.get(element, `payload.${key}`)
        )
    );
}

export const apiV6 = createApi({
    reducerData: "apiV6",
    baseQuery: fetchBaseQuery({
        baseUrl: `${baseUrl}api/v3`,
        prepareHeaders: (headers, { getState, endpoint }) => {
            headers.set("Access-Control-Allow-Origin", "*");
            headers.set("access-token", getState().auth.token);
            headers.set("track-token", getState().auth.user._id);
            return headers;
        },
    }),
    tagTypes: [
        "TrackerDep",
        "EngageDep",
        "NotifyDep",
        "Deck",
        "Leaderboard",
        "TicketInventory",
        "ArticleInventory",
        "CourseInventory",
        "CourseLearners",
        "ContestInventory",
        "ContestLearners",
        "EditingDeck",
        "SocialInventory",
        "UserInventory",
        "UserCoursesInventory",
        "TeamInventory",
        "TagsInventory",
        "QuestionDep",
        "BrandingDep",
        "BannerDep",
        "RazorPayDep",
    ],
    endpoints: (builder) => ({
        // #region Core Routes
        // ==========================================
        getBanners: builder.query({
            query: () => `/banners/published_list`,
            providesTags: ["BannerDep"],
        }),
        getSessions: builder.query({
            query: (id) => `/activity/${id}/sessions`,
            providesTags: ["TrackerDep"],
            async onQueryStarted(id, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(startSession(data));
                } catch (err) {
                    console.log("There was a session fetching error");
                }
            },
        }),
        getBranding: builder.query({
            query: (identifier) => `/teams/fetch/${identifier}`,
            providesTags: ["BrandingDep"],
            async onQueryStarted(id, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(updateBranding(data));
                } catch (err) {
                    console.log("There was a branding fetching error");
                }
            },
        }),
        sendBranding: builder.mutation({
            query: (prm) => ({
                url: `/teams/${prm.id}/update`,
                method: "POST",
                body: prm,
            }),
            invalidatesTags: ["BrandingDep"],
        }),
        //#endregion

        // #region Course Routes
        // ==========================================
        getCourseLibrary: builder.query({
            query: () => `/courseLibrary`,
        }),
        getProgram: builder.query({
            query: (id) => `/programs/${id}/show`,
            providesTags: ["TrackerDep"],
        }),
        getCourse: builder.query({
            query: (prm) => `/courses/${prm.id}/learners/${prm.user_id}/show`,
            providesTags: ["TrackerDep"],
            async onQueryStarted(prm, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(
                        startCourseAttempt({
                            id: prm.id,
                            currentState: data.currentState,
                            history: data.history,
                        })
                    );
                } catch (err) {
                    console.log("There was a course fetching error");
                }
            },
        }),
        getDeck: builder.query({
            query: (prm) =>
                `/decks/${prm.id}/learners/${prm.user_id}/show/${
                    prm.readerType || ""
                }`,
            providesTags: (result, error, arg) => {
                return [{ type: "Deck", id: arg.id }, "Deck"];
            },
            async onQueryStarted(prm, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(
                        startDeckAttempt({
                            id: prm.id,
                            currentState: data.currentState,
                            readerType: data.readerType,
                        })
                    );
                } catch (err) {
                    console.log("There was a deck fetching error");
                }
            },
        }),
        sendUserSearch: builder.mutation({
            query: (query) => ({
                url: `/users/list/search`,
                method: "POST",
                body: { query: query },
            }),
        }),
        sendAddLearner: builder.mutation({
            query: (prm) => ({
                url: `courses/${prm.id}/learners/${prm.user_id}/add`,
                method: "POST",
            }),
            invalidatesTags: ["CourseLearners", "UserCoursesInventory"],
        }),
        sendTeamEnrol: builder.mutation({
            query: (prm) => ({
                url: `courses/${prm.id}/learners/${prm.team}/add_team`,
                method: "POST",
            }),
            invalidatesTags: ["CourseLearners"],
        }),
        sendRemoveLearner: builder.mutation({
            query: (prm) => ({
                url: `courses/${prm.id}/learners/${prm.user_id}/remove`,
                method: "POST",
            }),
            invalidatesTags: ["CourseLearners", "UserCoursesInventory"],
        }),
        sendAddEnrollmentRule: builder.mutation({
            query: (prm) => ({
                url: `courses/${prm.id}/rules/add`,
                method: "POST",
                body: prm,
            }),
            invalidatesTags: ["CourseInventory"],
        }),
        sendRemoveEnrollmentRule: builder.mutation({
            query: (prm) => ({
                url: `courses/${prm.id}/rules/${prm.criteria_id}/remove`,
                method: "POST",
            }),
            invalidatesTags: ["CourseInventory"],
        }),
        sendRunEnrollmentRule: builder.mutation({
            query: (prm) => ({
                url: `courses/${prm.id}/rules/${prm.criteria_id}/run`,
                method: "POST",
            }),
            invalidatesTags: ["CourseInventory"],
        }),
        //#endregion

        // #region Search Routes
        // ==========================================
        sendSearchHistory: builder.mutation({
            query: (term) => ({
                url: `/searches/save`,
                method: "POST",
                body: { term: term },
            }),
        }),
        //#endregion

        // #region AI Routes
        // ==========================================
        getCredits: builder.query({
            query: () => `/ai/getcredits`,
        }),
        // #endregion

        // #region Notification Routes
        // ==========================================
        getNotifications: builder.query({
            query: (id) => `/users/${id}/notifications/fetch/all`,
            providesTags: ["NotifyDep"],
        }),
        getUnreadCount: builder.query({
            query: (id) => `/users/${id}/notifications/unread`,
            providesTags: ["NotifyDep"],
        }),
        sendNotificationsRead: builder.mutation({
            query: (id) => ({
                url: `/users/${id}/notifications/update/markread`,
                method: "POST",
            }),
            invalidatesTags: ["NotifyDep"],
        }),
        // #endregion

        // #region Article Routes
        // ==========================================
        getArticles: builder.query({
            query: () => `/articles/articleLibrary`,
            async onQueryStarted(prm, { dispatch, getState, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(
                        organizeArticles({
                            list: data.blog,
                            user: getState().auth.user,
                        })
                    );
                } catch (err) {
                    console.log("There was an articles fetching error");
                }
            },
        }),
        getArticle: builder.query({
            query: (prm) =>
                `/articles/${prm.id}/learners/${prm.user_id}/show/${
                    prm.readerType || ""
                }`,
            providesTags: ["EngageDep"],
        }),
        getUnauthArticle: builder.query({
            query: (identifier) => `/articles/${identifier}/unauth`,
            providesTags: ["EngageDep"],
        }),
        getRecentViews: builder.query({
            query: () => `/articles/recent`,
            providesTags: ["TrackerDep"],
        }),
        getFavorites: builder.query({
            query: () => `/articles/favorites`,
            providesTags: ["EngageDep"],
        }),
        sendArticlesComments: builder.mutation({
            query: (prm) => ({
                url: `/articles/${prm.id}/comments/create`,
                method: "POST",
                body: prm,
            }),
            invalidatesTags: ["EngageDep"],
        }),
        sendArticlesLikes: builder.mutation({
            query: (prm) => ({
                url: `/articles/${prm.id}/like`,
                method: "POST",
                body: prm,
            }),
            invalidatesTags: ["EngageDep"],
        }),
        sendArticlesDislikes: builder.mutation({
            query: (prm) => ({
                url: `/articles/${prm.id}/dislike`,
                method: "POST",
                body: prm,
            }),
            invalidatesTags: ["EngageDep"],
        }),
        sendArticlesBookmark: builder.mutation({
            query: (prm) => ({
                url: `/articles/${prm.id}/bookmark`,
                method: "POST",
            }),
            invalidatesTags: ["EngageDep"],
        }),
        getTags: builder.query({
            query: () => `/tags/list`,
            providesTags: ["TagsInventory"],
        }),

        // #endregion

        // #region Question Hangler Routes
        // ==========================================
        getQuestionResponse: builder.query({
            query: (prm) =>
                `/analytics/questions/${prm.deckId}/${prm.userId}/responses`,
            providesTags: ["QuestionDep"],
        }),

        // #endregion

        // #region Data Tracker Routes
        // ==========================================
        sendTracker: builder.mutation({
            query: (tracker) => ({
                url: "/activity/submit_queue",
                method: "POST",
                body: tracker,
            }),
            invalidatesTags: (result, error, arg) => {
                let invalidatables = [
                    { type: "TrackerDep" },
                    { type: "QuestionDep" },
                ];
                const deckValues = getChanged(result, "deck");
                _.each(deckValues, (deck) => {
                    invalidatables.push({ type: "Deck", id: deck });
                });
                return invalidatables;
            },
        }),
        sendErrors: builder.mutation({
            query: (tracker) => ({
                url: "/activity/submit_error",
                method: "POST",
                body: tracker,
            }),
            invalidatesTags: (result, error, arg) => {
                let invalidatables = [
                    { type: "TrackerDep" },
                    { type: "QuestionDep" },
                ];
                const deckValues = getChanged(result, "deck");
                _.each(deckValues, (deck) => {
                    invalidatables.push({ type: "Deck", id: deck });
                });
                return invalidatables;
            },
        }),
        // #endregion

        // #region Leaderboard Routes
        // ==========================================
        getAppLeaderboard: builder.query({
            query: () => `/leaderboards/appleaderboard`,
            providesTags: ["TrackerDep", "Leaderboard"],
        }),
        getPointsHistory: builder.query({
            query: (id) => `/analytics/${id}/pointsHistory`,
            providesTags: ["TrackerDep", "Leaderboard"],
        }),
        sendPointsHistoryFix: builder.mutation({
            query: (id) => ({
                url: `/analytics/${id}/fixPointsHistory`,
                method: "POST",
            }),
            invalidatesTags: ["TrackerDep", "Leaderboard"],
        }),
        getCourseLeaderboard: builder.query({
            query: (id) => `/courses/${id}/leaderboards/show`,
            providesTags: ["TrackerDep", "Leaderboard"],
        }),
        getCourseWallet: builder.query({
            query: (prm) =>
                `/courses/${prm.id}/leaderboards/wallet/${prm.user_id}`,
            providesTags: ["TrackerDep", "Leaderboard"],
        }),
        sendCourseWalletFix: builder.mutation({
            query: (prm) => ({
                url: `/courses/${prm.id}/leaderboards/wallet/${prm.user_id}/fix`,
                method: "POST",
            }),
            invalidatesTags: ["TrackerDep", "Leaderboard"],
        }),
        getDeckLeaderboard: builder.query({
            query: (id) => `/decks/${id}/leaderboards/allParameters`,
            providesTags: ["TrackerDep", "Leaderboard"],
        }),
        getCollectibles: builder.query({
            query: (id) => `/users/${id}/myprofile`,
            providesTags: ["TrackerDep", "Leaderboard"],
        }),
        // #endregion

        // #region Analytics Routes
        // ==========================================
        getLearnerProgress: builder.query({
            query: (id) => `/analytics/${id}/progress`,
            providesTags: ["TrackerDep"],
        }),
        getLearnerCourseProgress: builder.query({
            query: (prm) => `/analytics/${prm.userId}/${prm.courseId}/progress`,
            providesTags: ["TrackerDep"],
        }),
        getLearnerPerformance: builder.query({
            query: (id) => `/analytics/${id}/performance`,
            providesTags: ["TrackerDep"],
        }),
        getLearnerCoursePerformance: builder.query({
            query: (prm) =>
                `/analytics/${prm.userId}/${prm.courseId}/performance`,
            providesTags: ["TrackerDep"],
        }),
        getActivityLog: builder.query({
            query: (id) => `/activity/${id}/log`,
            providesTags: ["TrackerDep"],
        }),
        getDeckLearnerData: builder.query({
            query: (prm) =>
                `/analytics/${prm.userId}/${prm.courseId}/${prm.deckId}/learners/${prm.scope}`,
            providesTags: ["TrackerDep"],
        }),
        getArticleLearnerData: builder.query({
            query: (prm) =>
                `/analytics/articles/${prm.articleId}/deck/${prm.scope}`,
            providesTags: ["TrackerDep"],
        }),
        getCoursesStatus: builder.query({
            query: (userId) => `/analytics/${userId}/coursesStatus`,
            providesTags: ["TrackerDep"],
        }),
        getAssessmentsStatus: builder.query({
            query: (prm) =>
                `/analytics/${prm.courseId}/${prm.userId}/assessmentsStatus`,
            providesTags: ["TrackerDep"],
        }),
        getCourseLearnersData: builder.query({
            query: (prm) =>
                `/analytics/${prm.userId}/${prm.courseId}/learners/${prm.scope}`,
        }),
        getSurveyStatusData: builder.query({
            query: (prm) =>
                `/analytics/questions/${prm.deckId}/responses/learners`,
            providesTags: ["TrackerDep"],
        }),
        getQuestionsData: builder.query({
            query: (prm) =>
                `/analytics/questions/${prm.deckId}/responses/${prm.groupBy}`,
            providesTags: ["TrackerDep"],
        }),
        getArticleAnalytics: builder.query({
            query: (prm) => `/analytics/articles/${prm.id}/${prm.scope}`,
            providesTags: ["ArticleInventory"],
        }),
        getArticleLearnerResponse: builder.query({
            query: (prm) =>
                `analytics/questions/article/${prm.id}/responses/learners`,
            providesTags: ["TrackerDep"],
        }),
        getArticleQuestionResponse: builder.query({
            query: (prm) =>
                `analytics/questions/article/${prm.id}/responses/questions`,
            providesTags: ["TrackerDep"],
        }),
        // #endregion

        // #region Edit Profile Routes
        // ==========================================
        sendAcceptTerms: builder.mutation({
            query: (id) => ({
                url: `/users/${id}/update/acceptterms`,
                method: "POST",
            }),
            async onQueryStarted(prm, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(acceptTerms());
                } catch (err) {
                    console.log("There was a user update error");
                }
            },
        }),
        sendUserSelfUpdate: builder.mutation({
            query: (prm) => ({
                url: `/users/${prm.id}/update/self`,
                method: "POST",
                body: prm,
            }),
            async onQueryStarted(prm, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(
                        updateUser({
                            user: {
                                first_name: data.first_name,
                                last_name: data.last_name,
                                lang: data.lang,
                                avatarConfig: data.avatarConfig,
                                image: data.image,
                            },
                        })
                    );
                } catch (err) {
                    console.log("There was a user update error");
                }
            },
            invalidatesTags: ["Leaderboard"],
        }),
        // #endregion

        // #region Social Routes
        // ==========================================
        getPosts: builder.query({
            query: () => `/social/feed`,
            providesTags: ["SocialInventory"],
        }),
        sendSocialPost: builder.mutation({
            query: (post) => ({
                url: `/social/posts/create`,
                method: "POST",
                body: post,
            }),
            invalidatesTags: ["SocialInventory"],
        }),
        sendSocialComments: builder.mutation({
            query: (prm) => ({
                url: `/social/posts/${prm.id}/comments/create`,
                method: "POST",
                body: prm,
            }),
            invalidatesTags: ["SocialInventory"],
        }),
        sendSocialLikes: builder.mutation({
            query: (prm) => ({
                url: `/social/posts/${prm.id}/like`,
                method: "POST",
                body: prm,
            }),
            invalidatesTags: ["SocialInventory"],
        }),
        sendSocialDelete: builder.mutation({
            query: (id) => ({
                url: `/social/posts/${id}/delete`,
                method: "POST",
            }),
            invalidatesTags: ["SocialInventory"],
        }),

        // #endregion

        // #region Ticket Routes
        // ==========================================
        getTickets: builder.query({
            query: (id) => `/tickets/user/${id}`,
            providesTags: ["TicketInventory"],
        }),
        submitTicket: builder.mutation({
            query: (prm) => ({
                url: `/ticket/create`,
                method: "POST",
                body: prm,
            }),
            invalidatesTags: ["TicketInventory"],
        }),

        // #endregion

        // #region Marketplace Routes
        // ==========================================
        getStoreList: builder.query({
            query: () => `/files/getList`,
        }),

        // #endregion

        // #region Creator Routes
        // ==========================================
        getCourseInventory: builder.query({
            query: () => `/courses/list`,
            providesTags: ["CourseInventory"],
        }),
        getCourseBuilder: builder.query({
            query: (id) => `/courses/${id}/show`,
            providesTags: ["CourseInventory"],
        }),
        duplicateCourse: builder.mutation({
            query: (id) => ({
                url: `/courses/${id}/duplicate`,
                method: "POST",
            }),
            invalidatesTags: ["CourseInventory"],
        }),
        updateCourse: builder.mutation({
            query: (prm) => ({
                url: `/courses/${prm.id}/update`,
                method: "POST",
                body: prm,
            }),
            invalidatesTags: [
                "ArticleInventory",
                "CourseInventory",
                "TrackerDep",
            ],
        }),
        launchCourse: builder.mutation({
            query: (prm) => ({
                url: `/courses/${prm.id}/launch`,
                method: "POST",
            }),
            invalidatesTags: [
                "ArticleInventory",
                "CourseInventory",
                "TrackerDep",
            ],
        }),
        unlaunchCourse: builder.mutation({
            query: (prm) => ({
                url: `/courses/${prm.id}/unlaunch`,
                method: "POST",
            }),
            invalidatesTags: [
                "ArticleInventory",
                "CourseInventory",
                "TrackerDep",
            ],
        }),
        deleteTopic: builder.mutation({
            query: (prm) => ({
                url: `/courses/${prm.courseId}/topics/${prm.id}/delete`,
                method: "POST",
            }),
            invalidatesTags: ["CourseInventory"],
        }),
        deleteCourse: builder.mutation({
            query: (id) => ({
                url: `/courses/${id}/delete`,
                method: "POST",
            }),
            invalidatesTags: ["CourseInventory"],
        }),

        getCourseLearners: builder.query({
            query: (id) => `/courses/${id}/learners/list`,
            providesTags: ["CourseLearners"],
        }),

        getProgramInventory: builder.query({
            query: () => `/programs/list`,
            providesTags: ["CourseInventory"],
        }),
        getProgramBuilder: builder.query({
            query: (id) => `/programs/${id}/show`,
            providesTags: ["CourseInventory"],
        }),
        updateProgram: builder.mutation({
            query: (prm) => ({
                url: `/programs/${prm.id}/update`,
                method: "POST",
                body: prm,
            }),
            invalidatesTags: ["CourseInventory"],
        }),
        deleteProgram: builder.mutation({
            query: (id) => ({
                url: `/programs/${id}/delete`,
                method: "POST",
            }),
            invalidatesTags: ["CourseInventory"],
        }),

        createNewContent: builder.mutation({
            query: (prm) => ({
                url: `/${prm.contentType}/create`,
                method: "POST",
                body: _.omit(prm, "contentType"),
            }),
            invalidatesTags: ["CourseInventory", "ArticleInventory"],
        }),

        getArticleBuilder: builder.query({
            query: (id) => `/articles/${id}/showAdmin`,
            providesTags: ["ArticleInventory"],
        }),
        getArticleInventory: builder.query({
            query: () => `/articles/list`,
            providesTags: ["ArticleInventory"],
        }),
        deleteArticle: builder.mutation({
            query: (id) => ({
                url: `/articles/${id}/delete`,
                method: "POST",
            }),
            invalidatesTags: ["ArticleInventory"],
        }),
        duplicateArticle: builder.mutation({
            query: (id) => ({
                url: `/articles/${id}/duplicate`,
                method: "POST",
            }),
            invalidatesTags: ["ArticleInventory"],
        }),
        updateArticle: builder.mutation({
            query: (prm) => ({
                url: `/articles/${prm.id}/update`,
                method: "POST",
                body: prm,
            }),
            invalidatesTags: ["ArticleInventory", "EngageDep"],
        }),

        createDeck: builder.mutation({
            query: (prm) => ({
                url: `/decks/create`,
                method: "POST",
                body: prm,
            }),
            invalidatesTags: ["CourseInventory", "ArticleInventory"],
        }),
        getDeckBuilder: builder.query({
            query: (id) => `/decks/${id}/showAdmin`,
            providesTags: ["EditingDeck"],
        }),
        updateDeck: builder.mutation({
            query: (prm) => ({
                url: `/decks/${prm.id}/update`,
                method: "POST",
                body: prm,
            }),
            invalidatesTags: [
                "EditingDeck",
                "CourseInventory",
                "ArticleInventory",
            ],
        }),
        duplicateDeck: builder.mutation({
            query: (id) => ({
                url: `/decks/${id}/duplicate`,
                method: "POST",
            }),
            invalidatesTags: [
                "EditingDeck",
                "CourseInventory",
                "ArticleInventory",
            ],
        }),
        moveDeck: builder.mutation({
            query: (prm) => ({
                url: `/decks/${prm.id}/move/${prm.direction}`,
                method: "POST",
            }),
            invalidatesTags: ["CourseInventory"],
        }),
        deleteDeck: builder.mutation({
            query: (id) => ({
                url: `/decks/${id}/delete`,
                method: "POST",
            }),
            invalidatesTags: ["CourseInventory"],
        }),

        getContestInventory: builder.query({
            query: () => `/contests/list`,
            providesTags: ["ContestInventory"],
        }),
        getContestBuilder: builder.query({
            query: (id) => `/contests/${id}/show`,
            providesTags: ["ContestInventory"],
        }),
        updateContest: builder.mutation({
            query: (prm) => ({
                url: `/contests/${prm.id}/update`,
                method: "POST",
                body: prm,
            }),
            invalidatesTags: ["ContestInventory", "TrackerDep"],
        }),
        deleteContest: builder.mutation({
            query: (id) => ({
                url: `/contests/${id}/delete`,
                method: "POST",
            }),
            invalidatesTags: ["ContestInventory"],
        }),

        getContestLearners: builder.query({
            query: (id) => `/contests/${id}/learners/list`,
            providesTags: ["ContestLearners"],
        }),

        getUserCount: builder.query({
            query: () => `/users/count`,
            providesTags: ["UserInventory"],
        }),
        // #endregion

        // #region Admin Routes
        // ==========================================
        getCreators: builder.query({
            query: () => `/users/list/creators`,
            providesTags: ["TeamInventory"],
        }),
        getTeamsList: builder.query({
            query: () => `/teams/list`,
            providesTags: ["TeamInventory"],
        }),
        getTeamUsers: builder.query({
            query: (identifier) => `/users/list/team/${identifier}`,
            providesTags: ["UserInventory"],
        }),
        createTeam: builder.mutation({
            query: (prm) => ({
                url: `/teams/create`,
                method: "POST",
                body: prm,
            }),
            invalidatesTags: ["TeamInventory"],
        }),
        deleteTeam: builder.mutation({
            query: (id) => ({
                url: `/teams/${id}/delete`,
                method: "POST",
            }),
            invalidatesTags: ["TeamInventory"],
        }),
        createTag: builder.mutation({
            query: (prm) => ({
                url: `/tags/create`,
                method: "POST",
                body: prm,
            }),
            invalidatesTags: ["TagsInventory"],
        }),
        updateTag: builder.mutation({
            query: (prm) => ({
                url: `/tags/${prm.id}/update`,
                method: "POST",
                body: prm,
            }),
            invalidatesTags: ["TagsInventory"],
        }),
        deleteTag: builder.mutation({
            query: (id) => ({
                url: `/tags/${id}/delete`,
                method: "POST",
            }),
            invalidatesTags: ["TagsInventory"],
        }),
        getAllUsers: builder.query({
            query: () => `/users/list/adminList`,
            providesTags: ["UserInventory"],
        }),
        getAllCreators: builder.query({
            query: () => `/users/list/creatorList`,
            providesTags: ["UserInventory"],
        }),
        getPaginatedUsers: builder.query({
            query: (prm) =>
                `/users/list/paginated/${prm.page}/${prm.pageSize}${
                    prm.search ? `/${prm.search}` : ""
                }`,
            providesTags: ["UserInventory"],
        }),
        createUser: builder.mutation({
            query: (prm) => ({
                url: `/users/createnroll`,
                method: "POST",
                body: prm,
            }),
            invalidatesTags: ["UserInventory"],
        }),
        getUser: builder.query({
            query: (id) => `/users/${id}/showAdmin`,
            providesTags: ["UserInventory"],
        }),
        getUserCourses: builder.query({
            query: (id) => `/users/${id}/show/courses`,
            providesTags: ["UserCoursesInventory"],
        }),
        editUser: builder.mutation({
            query: (prm) => ({
                url: `/users/${prm.id}/update/super`,
                method: "POST",
                body: prm,
            }),
            invalidatesTags: ["UserInventory"],
        }),
        activateUser: builder.mutation({
            query: (id) => ({
                url: `/users/${id}/activate`,
                method: "POST",
            }),
            invalidatesTags: ["UserInventory"],
        }),
        deactivateUser: builder.mutation({
            query: (id) => ({
                url: `/users/${id}/deactivate`,
                method: "POST",
            }),
            invalidatesTags: ["UserInventory"],
        }),
        deleteUser: builder.mutation({
            query: (id) => ({
                url: `/users/${id}/delete`,
                method: "POST",
            }),
            invalidatesTags: ["UserInventory"],
        }),
        getBannerList: builder.query({
            query: () => `/banners/list`,
            providesTags: ["BannerDep"],
        }),
        createBanner: builder.mutation({
            query: (prm) => ({
                url: `/banners/create`,
                method: "POST",
                body: prm,
            }),
            invalidatesTags: ["BannerDep"],
        }),
        sendBanner: builder.mutation({
            query: (prm) => ({
                url: `/banners/${prm.id}/update`,
                method: "POST",
                body: prm,
            }),
            invalidatesTags: ["BannerDep"],
        }),
        deleteBanner: builder.mutation({
            query: (id) => ({
                url: `/banners/${id}/delete`,
                method: "POST",
            }),
            invalidatesTags: ["BannerDep"],
        }),
        getTicketList: builder.query({
            query: () => `/tickets/list`,
            providesTags: ["TicketInventory"],
        }),
        updateTicket: builder.mutation({
            query: (prm) => ({
                url: `/tickets/${prm.id}/update`,
                method: "POST",
                body: prm,
            }),
            invalidatesTags: ["TicketInventory"],
        }),
        getAncestors: builder.query({
            query: (id) => `/users/${id}/hierarchy/ancestors`,
        }),
        getReportees: builder.query({
            query: (id) => `/users/${id}/hierarchy/children`,
        }),
        getDescendants: builder.query({
            query: (id) => `/users/${id}/hierarchy/descendants`,
        }),
        // #endregion

        // #region Admin Analytics Routes
        // ==========================================
        getReportList: builder.query({
            query: (section) => `/analytics/reports/saved/getList/${section}`,
        }),
        getVersionList: builder.query({
            query: (id) => `/analytics/reports/saved/getVersion/${id}`,
        }),
        // #endregion

        // #region RazorPay Routes
        // ==========================================
        createSubscription: builder.mutation({
            query: (plan) => ({
                url: `/utility/payments/create_subscription/${plan.id}`,
                method: "POST",
                body: plan,
            }),
            invalidatesTags: ["RazorPayDep"],
        }),
        verifySubscription: builder.mutation({
            query: (res) => ({
                url: `/utility/payments/verify_subscription`,
                method: "POST",
                body: res,
            }),
            invalidatesTags: ["RazorPayDep"],
        }),
        sendContactUsRequst: builder.mutation({
            query: (res) => ({
                url: `/staging/contactUsRequest`,
                method: "POST",
                body: res,
            }),
            invalidatesTags: ["RazorPayDep"],
        }),
        // #region Notification Routes
        // ==========================================
        createInNotification: builder.mutation({
            query: (prm) => ({
                url: `/users/${prm._id}/notifications/create`,
                method: "POST",
                body: { message: prm.message },
            }),
            invalidatesTags: ["UserInventory", "CourseInventory"],
        }),
        createEmailNotification: builder.mutation({
            query: (prm) => ({
                url: `/users/${prm._id}/email/${prm.route}`,
                method: "POST",
                body: {
                    message: prm?.message,
                    type: prm?.emailType,
                    course: prm?.course,
                    assignor: prm?.courseAssignor,
                },
            }),
            invalidatesTags: ["UserInventory", "CourseInventory"],
        }),

        // #endregion
    }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
    useSendTrackerMutation,
    useSendErrorsMutation,
    useGetBannersQuery,
    useGetBrandingQuery,
    useSendBrandingMutation,
    useGetCourseLibraryQuery,
    useGetArticlesQuery,
    useGetArticleQuery,
    useGetUnauthArticleQuery,
    useGetRecentViewsQuery,
    useGetFavoritesQuery,
    useGetCourseQuery,
    useGetProgramQuery,
    useGetDeckQuery,
    useSendUserSearchMutation,
    useSendAddLearnerMutation,
    useSendTeamEnrolMutation,
    useSendRemoveLearnerMutation,
    useSendAddEnrollmentRuleMutation,
    useSendRemoveEnrollmentRuleMutation,
    useSendRunEnrollmentRuleMutation,
    useGetSessionsQuery,
    useGetAppLeaderboardQuery,
    useGetPointsHistoryQuery,
    useSendPointsHistoryFixMutation,
    useGetCourseLeaderboardQuery,
    useGetCourseWalletQuery,
    useSendCourseWalletFixMutation,
    useGetDeckLeaderboardQuery,
    useGetCollectiblesQuery,
    useGetPostsQuery,
    useGetTagsQuery,
    useSendSearchHistoryMutation,
    useSendArticlesCommentsMutation,
    useSendArticlesLikesMutation,
    useSendArticlesDislikesMutation,
    useSendArticlesBookmarkMutation,
    useGetNotificationsQuery,
    useGetUnreadCountQuery,
    useSendNotificationsReadMutation,
    useSendSocialPostMutation,
    useSendSocialCommentsMutation,
    useSendSocialLikesMutation,
    useSendSocialDeleteMutation,
    useGetLearnerProgressQuery,
    useGetLearnerCourseProgressQuery,
    useGetLearnerPerformanceQuery,
    useGetLearnerCoursePerformanceQuery,
    useGetActivityLogQuery,
    useSendUserSelfUpdateMutation,
    useSendAcceptTermsMutation,
    useGetCourseInventoryQuery,
    useGetProgramInventoryQuery,
    useGetProgramBuilderQuery,
    useGetCourseBuilderQuery,
    useGetArticleBuilderQuery,
    useGetArticleInventoryQuery,
    useDeleteArticleMutation,
    useDuplicateArticleMutation,
    useUpdateArticleMutation,
    useCreateNewContentMutation,
    useGetDeckBuilderQuery,
    useUpdateDeckMutation,
    useUpdateCourseMutation,
    useDuplicateCourseMutation,
    useLaunchCourseMutation,
    useUnlaunchCourseMutation,
    useDuplicateDeckMutation,
    useCreateDeckMutation,
    useMoveDeckMutation,
    useDeleteDeckMutation,
    useDeleteTopicMutation,
    useGetDeckLearnerDataQuery,
    useGetArticleLearnerDataQuery,
    useDeleteCourseMutation,
    useGetCourseLearnersQuery,
    useUpdateProgramMutation,
    useDeleteProgramMutation,
    useGetCourseLearnersDataQuery,
    useGetSurveyStatusDataQuery,
    useGetContestBuilderQuery,
    useGetContestInventoryQuery,
    useDeleteContestMutation,
    useGetContestLearnersQuery,
    useUpdateContestMutation,
    useSubmitTicketMutation,
    useGetTicketsQuery,
    useGetTeamUsersQuery,
    useGetAllUsersQuery,
    useGetPaginatedUsersQuery,
    useGetTeamsListQuery,
    useCreateUserMutation,
    useEditUserMutation,
    useCreateTeamMutation,
    useDeleteTeamMutation,
    useCreateTagMutation,
    useDeleteTagMutation,
    useUpdateTagMutation,
    useDeactivateUserMutation,
    useActivateUserMutation,
    useDeleteUserMutation,
    useGetUserQuery,
    useGetUserCoursesQuery,
    useGetQuestionResponseQuery,
    useGetCreditsQuery,
    useGetCoursesStatusQuery,
    useGetAssessmentsStatusQuery,
    useGetArticleAnalyticsQuery,
    useGetArticleLearnerResponseQuery,
    useGetArticleQuestionResponseQuery,
    useGetQuestionsDataQuery,
    useGetBannerListQuery,
    useGetAncestorsQuery,
    useGetReporteesQuery,
    useGetDescendantsQuery,
    useGetTicketListQuery,
    useSendBannerMutation,
    useCreateBannerMutation,
    useDeleteBannerMutation,
    useUpdateTicketMutation,
    useGetReportListQuery,
    useGetVersionListQuery,
    useGetAllCreatorsQuery,
    useGetCreatorsQuery,
    useGetUserCountQuery,
    useCreateSubscriptionMutation,
    useVerifySubscriptionMutation,
    useSendContactUsRequstMutation,
    useGetStoreListQuery,
    useCreateInNotificationMutation,
    useCreateEmailNotificationMutation,
} = apiV6;
